import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, TrendingUp, TrendingDown } from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { api, cls, fmtAum, fmtNav, fmtPct } from "@/lib/api";

function CustomTooltip({ active, payload, label }) {
  if (active && payload && payload.length) {
    const dataPoint = payload[0].payload;
    return (
      <div className="bg-white border border-sif rounded-lg p-3 card-shadow text-xs min-w-[220px]">
        <div className="font-semibold text-sif-primary mb-2 border-b border-sif pb-1">{label}</div>
        <div className="space-y-2">
          {payload.map((item) => {
            const val = item.value;
            const name = item.name;
            const color = item.stroke || item.color;
            let displayVal = "";
            let returnVal = "";
            let rawValText = "";

            if (item.dataKey === "nav") {
              displayVal = fmtNav(val);
              if (dataPoint.fundReturn !== undefined) {
                const sign = dataPoint.fundReturn >= 0 ? "+" : "";
                returnVal = ` (${sign}${dataPoint.fundReturn.toFixed(2)}%)`;
              }
            } else if (item.dataKey === "nifty500tr") {
              displayVal = fmtNav(val);
              if (dataPoint.nifty500Return !== undefined) {
                const sign = dataPoint.nifty500Return >= 0 ? "+" : "";
                returnVal = ` (${sign}${dataPoint.nifty500Return.toFixed(2)}%)`;
              }
              if (dataPoint.nifty500tr_raw !== undefined && dataPoint.nifty500tr_raw !== null) {
                rawValText = ` (Index: ${dataPoint.nifty500tr_raw.toLocaleString("en-IN", { maximumFractionDigits: 1 })})`;
              }
            } else if (item.dataKey === "nifty100") {
              displayVal = fmtNav(val);
              if (dataPoint.nifty100Return !== undefined) {
                const sign = dataPoint.nifty100Return >= 0 ? "+" : "";
                returnVal = ` (${sign}${dataPoint.nifty100Return.toFixed(2)}%)`;
              }
              if (dataPoint.nifty100_raw !== undefined && dataPoint.nifty100_raw !== null) {
                rawValText = ` (Index: ${dataPoint.nifty100_raw.toLocaleString("en-IN", { maximumFractionDigits: 1 })})`;
              }
            }

            const returnCls = item.dataKey === "nav" 
              ? cls(dataPoint.fundReturn) 
              : (item.dataKey === "nifty500tr" ? cls(dataPoint.nifty500Return) : cls(dataPoint.nifty100Return));

            return (
              <div key={item.dataKey} className="flex flex-col">
                <div className="flex items-center justify-between gap-4">
                  <span className="flex items-center gap-1.5 text-sif-muted">
                    <span className="w-2.5 h-0.5 rounded-full" style={{ backgroundColor: color, display: "inline-block" }}></span>
                    {name}
                  </span>
                  <span className="font-semibold font-mono text-sif-primary">
                    {displayVal}
                    <span className={`ml-1 font-bold ${returnCls}`}>
                      {returnVal}
                    </span>
                  </span>
                </div>
                {rawValText && (
                  <div className="text-[10px] text-sif-muted pl-4 mt-0.5">
                    {rawValText}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
  return null;
}
import RiskMetrics from "@/components/RiskMetrics";
import DrawdownChart from "@/components/DrawdownChart";
import RollingReturnsChart from "@/components/RollingReturnsChart";

const PERIODS = ["1W", "1M", "3M", "6M", "1Y", "SI"];

export default function FundDetail() {
  const { code } = useParams();
  const [fund, setFund] = useState(null);
  const [rolling, setRolling] = useState(null);
  const [navPeriod, setNavPeriod] = useState("1Y");
  const [navSeries, setNavSeries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [f, r] = await Promise.all([
        api.get(`/funds/${code}`),
        api.get(`/funds/${code}/rolling-returns`),
      ]);
      setFund(f.data);
      setRolling(r.data);
      setLoading(false);
    })();
  }, [code]);

  useEffect(() => {
    api.get(`/funds/${code}/nav`, { params: { period: navPeriod } }).then((r) => {
      const rawPoints = r.data.points;
      if (!rawPoints || rawPoints.length === 0) {
        setNavSeries([]);
        return;
      }

      const firstFundNav = rawPoints[0].nav || 1;
      const firstNifty500 = rawPoints[0].nifty500tr || 1;
      const firstNifty100 = rawPoints[0].nifty100 || 1;

      const points = rawPoints.map((p) => {
        const fundRet = ((p.nav - firstFundNav) / firstFundNav) * 100;
        const n500Ret = p.nifty500tr ? ((p.nifty500tr - firstNifty500) / firstNifty500) * 100 : 0;
        const n100Ret = p.nifty100 ? ((p.nifty100 - firstNifty100) / firstNifty100) * 100 : 0;

        return {
          date: p.date.slice(0, 10),
          nav: p.nav,
          fundReturn: fundRet,
          nifty500tr: p.nifty500tr ? (p.nifty500tr / firstNifty500) * firstFundNav : null,
          nifty500tr_raw: p.nifty500tr,
          nifty500Return: n500Ret,
          nifty100: p.nifty100 ? (p.nifty100 / firstNifty100) * firstFundNav : null,
          nifty100_raw: p.nifty100,
          nifty100Return: n100Ret,
        };
      });
      setNavSeries(points);
    });
  }, [code, navPeriod]);

  if (loading || !fund) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-16 text-center text-sif-muted">
        Loading fund details…
      </div>
    );
  }

  const isPositive = (fund.return_1y ?? 0) >= 0;

  const returnTiles = [
    { label: "1 Week", value: rolling?.period_1w },
    { label: "1 Month", value: rolling?.period_1m },
    { label: "3 Months", value: rolling?.period_3m },
    { label: "6 Months", value: rolling?.period_6m },
    { label: "YTD", value: rolling?.period_ytd },
    { label: "1 Year", value: rolling?.period_1y },
    { label: "Since Inception", value: rolling?.since_inception },
  ];

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      <Link
        to="/"
        data-testid="back-link"
        className="inline-flex items-center gap-1.5 text-sm text-sif-muted hover:text-sif-primary transition-colors mb-5"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Funds
      </Link>

      {/* Fund header */}
      <section data-testid="fund-header" className="bg-white border border-sif rounded-xl p-6 card-shadow mb-6">
        <div className="flex flex-wrap items-start justify-between gap-5">
          <div>
            <div className="text-xs text-sif-muted font-medium uppercase tracking-wider">{fund.amc}</div>
            <h1 className="mt-1 text-2xl md:text-3xl font-bold text-sif-primary">{fund.name}</h1>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <span className="px-2.5 py-1 rounded-md bg-slate-100 text-xs font-medium text-sif-text">
                {fund.category}
              </span>
              <span className="px-2.5 py-1 rounded-md bg-sif-secondary/10 text-xs font-medium text-sif-secondary">
                Benchmark · {fund.benchmark}
              </span>
              <span className="text-xs text-sif-muted">
                TER {fund.ter}% · Min ₹{fund.min_investment.toLocaleString("en-IN")}
              </span>
            </div>
          </div>

          <div className="text-right">
            <div className="text-xs text-sif-muted uppercase tracking-wider">Latest NAV</div>
            <div className="text-3xl font-bold text-sif-primary font-mono">{fmtNav(fund.latest_nav)}</div>
            <div className={`mt-1 flex items-center justify-end gap-1 text-sm font-semibold ${cls(fund.day_change_pct)}`}>
              {(fund.day_change_pct ?? 0) >= 0 ? (
                <TrendingUp className="w-4 h-4" />
              ) : (
                <TrendingDown className="w-4 h-4" />
              )}
              {fmtPct(fund.day_change_pct)} today
            </div>
          </div>
        </div>
      </section>

      {/* NAV chart */}
      <section
        data-testid="nav-chart-section"
        className="bg-white border border-sif rounded-xl p-6 card-shadow mb-6"
      >
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div>
            <div className="text-xs text-sif-muted uppercase tracking-wider font-semibold">NAV Performance</div>
            <div className="text-lg font-semibold text-sif-primary mt-0.5">{navPeriod} chart</div>
          </div>
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1" data-testid="period-selector">
            {PERIODS.map((p) => (
              <button
                key={p}
                data-testid={`period-btn-${p}`}
                onClick={() => setNavPeriod(p)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                  navPeriod === p
                    ? "bg-white text-sif-primary card-shadow"
                    : "text-sif-muted hover:text-sif-primary"
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        <div className="h-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={navSeries} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="navFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={isPositive ? "#14B8A6" : "#EF4444"} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={isPositive ? "#14B8A6" : "#EF4444"} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: "#64748B" }}
                stroke="#CBD5E1"
                minTickGap={40}
              />
              <YAxis
                domain={["dataMin", "dataMax"]}
                tick={{ fontSize: 11, fill: "#64748B" }}
                stroke="#CBD5E1"
                tickFormatter={(v) => (v >= 100 ? v.toFixed(0) : v.toFixed(2))}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: "12px", paddingTop: "12px" }}
                verticalAlign="bottom"
                align="center"
              />
              <Area
                type="monotone"
                dataKey="nav"
                name={fund.name}
                stroke={isPositive ? "#14B8A6" : "#EF4444"}
                strokeWidth={2}
                fill="url(#navFill)"
              />
              <Line
                type="monotone"
                dataKey="nifty100"
                name="NIFTY 100 (Rebased)"
                stroke="#3B82F6"
                strokeWidth={1.5}
                strokeDasharray="4 4"
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="nifty500tr"
                name="NIFTY 500 TR (Rebased)"
                stroke="#64748B"
                strokeWidth={1.5}
                strokeDasharray="4 4"
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Rolling returns */}
      <section
        data-testid="rolling-returns-section"
        className="bg-white border border-sif rounded-xl p-6 card-shadow mb-6"
      >
        <div className="mb-5">
          <div className="text-xs text-sif-muted uppercase tracking-wider font-semibold">Rolling Returns</div>
          <div className="text-lg font-semibold text-sif-primary mt-0.5">
            Point-to-point returns across time horizons
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {returnTiles.map((t) => (
            <div
              key={t.label}
              data-testid={`return-tile-${t.label.replace(/\s+/g, "-").toLowerCase()}`}
              className="rounded-lg border border-sif p-4 bg-slate-50/60 card-hover"
            >
              <div className="text-[10px] text-sif-muted uppercase tracking-wider font-semibold">{t.label}</div>
              <div className={`mt-1.5 text-lg font-bold font-mono ${cls(t.value)}`}>{fmtPct(t.value)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Analytics sections */}
      <RiskMetrics code={code} />
      <DrawdownChart code={code} />
      <RollingReturnsChart code={code} />

      {/* Fund info */}
      <section
        data-testid="fund-info-section"
        className="bg-white border border-sif rounded-xl p-6 card-shadow"
      >
        <div className="mb-4">
          <div className="text-xs text-sif-muted uppercase tracking-wider font-semibold">Fund Information</div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <InfoRow label="AMC" value={fund.amc} />
          <InfoRow label="Category" value={fund.category} />
          <InfoRow label="Benchmark" value={fund.benchmark} />
          <InfoRow label="Launch Date" value={fund.launch_date?.slice(0, 10)} />
          <InfoRow label="AUM" value={fmtAum(fund.aum_cr)} />
          <InfoRow label="Min Investment" value={fund.min_investment_text || `₹${fund.min_investment.toLocaleString("en-IN")}`} />
          <InfoRow label="Expense Ratio" value={fund.ter ? `${fund.ter}%` : "—"} />
          <InfoRow label="Scheme Type" value={fund.scheme_type} />
          <InfoRow 
            label="AMC Website" 
            value={fund.amc_website ? (
              <a href={fund.amc_website} target="_blank" rel="noopener noreferrer" className="text-sif-secondary hover:underline">
                {fund.amc_website.replace("https://", "").replace("www.", "")}
              </a>
            ) : "—"} 
          />
          <InfoRow label="Fund Code" value={fund.code} mono />
        </div>

        {fund.objective && (
          <div className="mt-6 pt-6 border-t border-slate-100">
            <div className="text-xs text-sif-muted uppercase tracking-wider font-semibold mb-2">Scheme Objective</div>
            <p className="text-sm text-sif-text leading-relaxed whitespace-pre-line font-medium">{fund.objective}</p>
          </div>
        )}

        {fund.load_details && (
          <div className="mt-6 pt-6 border-t border-slate-100">
            <div className="text-xs text-sif-muted uppercase tracking-wider font-semibold mb-2">Exit Load Details</div>
            <p className="text-sm text-sif-text leading-relaxed whitespace-pre-line font-medium">{fund.load_details}</p>
          </div>
        )}
      </section>
    </div>
  );
}

function InfoRow({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] text-sif-muted uppercase tracking-wider font-semibold">{label}</div>
      <div className={`mt-1 text-sm font-semibold text-sif-primary ${mono ? "font-mono" : ""}`}>{value ?? "—"}</div>
    </div>
  );
}
